import { useEffect, useRef, useState, useId } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CustomAudioPlayerProps {
  src: string;
  title?: string;
  className?: string;
  statusHistory?: Array<{
    to: string;
    from?: string;
    date: string;
    note?: string;
    user: {
      name: string;
      role: string;
    };
  }>;
}

export function CustomAudioPlayer({ src, className, statusHistory }: CustomAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const playerId = useId();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    // Diğer player çaldığında bunu durdur
    const handleOtherPlay = (e: CustomEvent<string>) => {
      if (e.detail !== playerId && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    window.addEventListener("audio-play" as any, handleOtherPlay);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      window.removeEventListener("audio-play" as any, handleOtherPlay);
    };
  }, [playerId]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // Diğer player'ları durdur
      window.dispatchEvent(new CustomEvent("audio-play", { detail: playerId }));
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeChange = (value: number[]) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = value[0];
    setCurrentTime(value[0]);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className={cn("flex items-center gap-3", className)}>
        <button
          type="button"
          className="h-10 w-10 rounded-full flex-shrink-0 transition-all flex items-center justify-center hover:opacity-90"
          style={{ background: 'linear-gradient(to right, #6b21a8, #a855f7)' }}
          onClick={togglePlay}
        >
          {isPlaying ? (
            <div className="flex items-center gap-[3px]">
              <div className="w-[4px] h-[14px] bg-white rounded-sm" />
              <div className="w-[4px] h-[14px] bg-white rounded-sm" />
            </div>
          ) : (
            <div
              className="ml-[2px]"
              style={{
                width: 0,
                height: 0,
                borderTop: '7px solid transparent',
                borderBottom: '7px solid transparent',
                borderLeft: '12px solid white'
              }}
            />
          )}
        </button>
        <div className="flex-1 flex items-center gap-3">
          <Slider
            value={[currentTime]}
            min={0}
            max={duration || 100}
            step={0.1}
            onValueChange={handleTimeChange}
            className="cursor-pointer flex-1"
          />
          <div className="min-w-[80px] text-sm text-slate-600 font-medium tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
        {statusHistory && statusHistory.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
            onClick={() => setShowHistory(true)}
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
        )}
        <audio ref={audioRef} src={src} preload="metadata" />
      </div>

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revize Geçmişi</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              {statusHistory?.map((history, index) => (
                <div key={index} className="p-3 bg-muted rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className={cn({
                      "bg-green-100 text-green-700": history.to === 'approved',
                      "bg-yellow-100 text-yellow-700": history.to === 'pending_admin',
                      "bg-blue-100 text-blue-700": history.to === 'manager_review'
                    })}>
                      {history.to === 'approved' ? "Onaylandı" :
                       history.to === 'pending_admin' ? "Revize İstendi" :
                       history.to === 'manager_review' ? "Manager İncelemesi" :
                       history.to}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(history.date)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <span className="font-medium">{history.user.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {history.user.role === 'super_admin' ? 'Süper Admin' :
                       history.user.role === 'manager' ? 'Manager' :
                       history.user.role}
                    </Badge>
                  </div>
                  {history.note && (
                    <p className="text-sm mt-2 text-muted-foreground">{history.note}</p>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
